/**
 * What the file picker threw away, and why.
 *
 * react-dropzone's `useDropzone` returns `fileRejections`, and this component
 * did not read it. Anything the accept filter refused vanished with no message
 * to the customer and no report to Sentry — the only silent failure left in the
 * upload flow, since every other path (add-time snapshot, thumbnail, HEIC
 * conversion, upload) reports.
 *
 * That silence matters more than it looks. Across 14 days of upload failures
 * in Sentry — 12 "picked file unreadable", 9 "no photos uploaded", 11
 * "thumbnail generation failed" — exactly ONE event came from iOS, while iPhone
 * customers were reporting that selecting photos does nothing at all. Sentry
 * does capture from Mobile Safari, so the absence is not blindness in the SDK:
 * it means those customers fail BEFORE any instrumented code runs. The picker
 * is what runs before all of it.
 *
 * So the point of this module is the shape of what got rejected: `emptyTypeCount`
 * first, because iOS and in-app WebViews hand back picked images with an empty
 * `file.type`, and `extension` second, because when the type is empty the
 * filename is the only identity the file has left.
 */

/**
 * Structural mirror of react-dropzone's `FileRejection`, kept local so this
 * module stays testable without importing React. `errors` is `readonly` to
 * match it exactly — a mutable array here makes the whole callback fail to
 * typecheck against `onDropRejected`.
 */
export interface DropRejection {
  file: File;
  errors: readonly { code: string; message: string }[];
}

export interface RejectedFileInfo {
  name: string;
  size: number;
  /** Empty string is the interesting case, not a missing value. */
  type: string;
  /** Lowercased, no dot. "" when the name has no extension. */
  extension: string;
  /** react-dropzone codes, e.g. "file-invalid-type", "file-too-large". */
  codes: string[];
}

export interface RejectionSummary {
  /** Per-file detail, capped — the shape matters, not all 300 filenames. */
  files: RejectedFileInfo[];
  /** How many files each rejection reason accounted for. */
  codeCounts: Record<string, number>;
  /** Files whose MIME type was empty. The iOS / in-app WebView signature. */
  emptyTypeCount: number;
  /** Every rejected file, including those beyond the per-file cap. */
  total: number;
}

/** Enough files to see a pattern; few enough to stay a readable payload. */
const MAX_REPORTED_FILES = 10;

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return "";
  return name.slice(dot + 1).toLowerCase();
}

export function summariseRejections(
  rejections: DropRejection[],
): RejectionSummary {
  const codeCounts: Record<string, number> = {};
  let emptyTypeCount = 0;

  for (const { file, errors } of rejections) {
    if (!file.type) emptyTypeCount += 1;
    for (const { code } of errors) {
      codeCounts[code] = (codeCounts[code] ?? 0) + 1;
    }
  }

  const files = rejections.slice(0, MAX_REPORTED_FILES).map(
    ({ file, errors }): RejectedFileInfo => ({
      name: file.name,
      size: file.size,
      type: file.type,
      extension: extensionOf(file.name),
      codes: errors.map((e) => e.code),
    }),
  );

  return { files, codeCounts, emptyTypeCount, total: rejections.length };
}
